import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import "@fontsource-variable/instrument-sans";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import App from "./App";
import { BRAND } from "./brand";
import "./styles.css";
import "./ide.css";
import "./auth.css";

const requestedPath = window.location.pathname;
if (requestedPath === "/app" || (requestedPath.startsWith("/app/") && !["/app/home", "/app/ide"].includes(requestedPath))) {
  window.history.replaceState(null, "", "/app/home");
}

const isHomeRoute = window.location.pathname === "/app/home";
const isIdeRoute = window.location.pathname === "/app/ide";
const isAuthRoute = window.location.pathname === "/login" || window.location.pathname === "/signup";
const IDEApp = lazy(() => import("./IDEApp"));
const HomeApp = lazy(() => import("./HomeApp"));
const AuthApp = lazy(() => import("./AuthApp"));

const homeRouteFallback = (
  <div
    aria-busy="true"
    role="status"
    style={{
      alignItems: "center",
      background: "#050607",
      color: "#f5f6f7",
      display: "flex",
      flexDirection: "column",
      fontFamily: '"Instrument Sans Variable", sans-serif',
      gap: 12,
      justifyContent: "center",
      minHeight: "100dvh",
    }}
  >
    <strong style={{ fontSize: 18, letterSpacing: "0.11em" }}>{BRAND.name}</strong>
    <span style={{ color: "#8e969a", fontSize: 13 }}>Loading strategy library…</span>
  </div>
);

if (isHomeRoute) {
  const homeContract = document.createComment(`
    THESIS: Put fast resumption before management without turning a strategy library into a generic dashboard; refuse the card-grid home.
    OWN-WORLD: Premium near-black and graphite planes, cool-paper type, neutral hairlines, compact evidence mono, and sparse cyan only for focus, current state, and the primary action.
    STORY: See the latest work, create or import a strategy, optionally group it into a project, manage its lifecycle, then open one explicit strategy in the IDE.
    FIRST VIEWPORT: Compact account bar, heading and actions, one full-width Continue working band, then a project scope rail beside the searchable strategy ledger.
    FORM: Resume Horizon, third grounded form in the ordered set. Direction seed 16289a34.
    FINISH: unreviewed and undocumented is unfinished; this build ends with the finish review, the verdict, and DESIGN.md
  `);
  document.body.insertBefore(homeContract, document.body.firstChild);
}

if (isAuthRoute) {
  const authContract = document.createComment(`
    THESIS: Make authentication the quiet threshold into a serious research workspace; refuse the generic split-screen SaaS login.
    OWN-WORLD: Pitch-black field, restrained monochrome dot grid, charcoal single card, thin neutral borders, self-hosted product type, white action, spectral state color.
    STORY: Returning users sign in with lasting credentials; invited users verify a one-time code, then create the account they use thereafter.
    FIRST VIEWPORT: A centered 420px card holds brand, route-specific form, persistence or progress, and the product boundary; overview return sits upper-left.
    FORM: Single matte modal card on an atmospheric dot field. Auth seed auth-dot-threshold-20260809.
    FINISH: unreviewed and undocumented is unfinished; this build ends with the finish review, the verdict, and DESIGN.md
  `);
  document.body.insertBefore(authContract, document.body.firstChild);
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {isHomeRoute
      ? <Suspense fallback={homeRouteFallback}><HomeApp /></Suspense>
      : isIdeRoute
      ? <Suspense fallback={null}><IDEApp /></Suspense>
      : isAuthRoute
        ? <Suspense fallback={null}><AuthApp /></Suspense>
        : <App />}
  </React.StrictMode>,
);
